"use client";

import * as React from "react";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";

import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

export interface ChartDataPoint {
  date: string;
  count: number;
}

export type ChartTimeRangeValue = "7d" | "30d" | "90d";

interface ChartAreaInteractiveProps {
  data?: ChartDataPoint[];
  timeRange?: ChartTimeRangeValue;
  onTimeRangeChange?: (range: ChartTimeRangeValue) => void;
}

// Default empty data - will be replaced by actual data from props
const defaultChartData: ChartDataPoint[] = [];

const chartConfig = {
  appointments: {
    label: "Appointments",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

export function ChartAreaInteractive({ 
  data = defaultChartData, 
  timeRange: externalTimeRange,
  onTimeRangeChange 
}: ChartAreaInteractiveProps) {
  const isMobile = useIsMobile();
  const [internalTimeRange, setInternalTimeRange] = React.useState<ChartTimeRangeValue>("30d");
  
  // Use external timeRange if provided, otherwise use internal state
  const timeRange = externalTimeRange ?? internalTimeRange;
  
  const handleTimeRangeChange = (newRange: string) => {
    const range = newRange as ChartTimeRangeValue;
    if (onTimeRangeChange) {
      onTimeRangeChange(range);
    } else {
      setInternalTimeRange(range);
    }
  };

  React.useEffect(() => {
    if (isMobile && !externalTimeRange) {
      setInternalTimeRange("7d");
    }
  }, [isMobile, externalTimeRange]);

  const daysLabel =
    timeRange === "7d" ? "7 days" : timeRange === "30d" ? "30 days" : "90 days";

  // Filter data based on time range
  const filteredData = React.useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    const daysToSubtract = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - daysToSubtract);
    startDate.setHours(0, 0, 0, 0);

    return data.filter((item) => {
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= today;
    });
  }, [data, timeRange]);

  return (
    <Card className="@container/card">
      <CardHeader className="relative">
        <CardTitle>Total Appointments</CardTitle>
        <CardDescription>
          <span className="@[540px]/card:block hidden">
            Total for the last {daysLabel}
          </span>
          <span className="@[540px]/card:hidden">
            Last {daysLabel}
          </span>
        </CardDescription>
        <div className="absolute right-4 top-4">
          <ToggleGroup
            type="single"
            value={timeRange}
            onValueChange={handleTimeRangeChange}
            variant="outline"
            className="@[767px]/card:flex hidden"
          >
            <ToggleGroupItem value="7d" className="h-8 px-2.5">
              Last 7 days
            </ToggleGroupItem>
            <ToggleGroupItem value="30d" className="h-8 px-2.5">
              Last 30 days
            </ToggleGroupItem>
            <ToggleGroupItem value="90d" className="h-8 px-2.5">
              Last 90 days
            </ToggleGroupItem>
          </ToggleGroup>
          <Select value={timeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="@[767px]/card:hidden flex w-40" aria-label="Select a value">
              <SelectValue placeholder="Last 30 days" />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="7d" className="rounded-lg">
                Last 7 days
              </SelectItem>
              <SelectItem value="30d" className="rounded-lg">
                Last 30 days
              </SelectItem>
              <SelectItem value="90d" className="rounded-lg">
                Last 90 days
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
          <AreaChart data={filteredData}>
            <defs>
              <linearGradient id="fillAppointments" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={1.0} />
                <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                });
              }}
            />
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  labelFormatter={(value) => {
                    return new Date(value).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                    });
                  }}
                  indicator="dot"
                />
              }
            />
            <Area 
              dataKey="count" 
              type="natural" 
              fill="url(#fillAppointments)" 
              stroke="var(--color-appointments)" 
              stackId="a" 
            />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
